import { useEffect, useState } from "react";
import * as yup from "yup";

import { PhoneInput } from "../../components/Input/Input";
import { Section } from "../../components/layout/Section";
import { StorSelfButton } from "../../components/buttons/StorSelfButton";
import { Subtitle1, PageTitle } from "../../brand/typographies";
import { useFormImplementation } from "../../hooks/useFormImplementation";
import { useSuccessError } from "../../hooks/useSuccessError";
import agents from "../../api/agents";
import { captureException } from "../../utils/errorHandling";
import { Box } from "@mui/material";
import { VerificationState } from "./SmsVerificationWrapper";
import useToast from "../../hooks/useToast";

interface EnterPhoneNumberProps {
  phoneNumberState: string | null;
  setPhoneNumberState: React.Dispatch<React.SetStateAction<string | null>>;
  setSection: React.Dispatch<React.SetStateAction<VerificationState>>;
  section: VerificationState;
}

export const EnterPhoneNumber = ({
  setPhoneNumberState,
  setSection,
  section,
  phoneNumberState,
}: EnterPhoneNumberProps) => {
  const [submitting, setSubmitting] = useState(false);

  const { fieldProps, handleSubmit } = useFormImplementation(
    schema,
    Object.keys({ ...schema.fields }),
    { phoneNumber: phoneNumberState ?? "" }
  );
  const { toast } = useToast();

  const { onError } = useSuccessError();

  const onSubmit = async (formValues: any, e: any) => {
    setSubmitting(true);

    try {
      if (section === VerificationState.RESEND_OTP) {
        await agents.OTP.resendOTP();
        toast.success("Verification token resent.");
      } else {
        setPhoneNumberState(formValues.phoneNumber);
        await agents.OTP.sendOTP(formValues.phoneNumber);
      }
      setSection(VerificationState.VERIFY_OTP);
    } catch (error) {
      captureException(error);

      if (error instanceof Error) {
        onError(error.message || "Oops! Something went wrong.");
      } else {
        onError("An unexpected error occurred.");
      }
    } finally {
      setSubmitting(false);
    }

    // if (section === VerificationState.RESEND_OTP) {
    //   resendOtp();
    // } else {
    // }
    // setPhoneNumberState(formValues.phoneNumber);

    // try {
    //   await agents.OTP.sendOTP(formValues.phoneNumber);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PageTitle>
        {section === VerificationState.RESEND_OTP
          ? "Resend Code"
          : "Verify Number"}
      </PageTitle>

      <Subtitle1 sx={{ textAlign: "center" }}>
        {section === VerificationState.RESEND_OTP
          ? "Confirm Phone Number for SMS verification."
          : "Please enter the verification code that we sent to your phone via SMS:"}
      </Subtitle1>
      <Section sx={{ my: 4 }}>
        <PhoneInput
          fullWidth
          label="Phone Number"
          defaultValue={phoneNumberState}
          {...fieldProps.phoneNumber}
        />
      </Section>

      <Box display={"flex"} gap={1}>
        {section === VerificationState.RESEND_OTP && (
          <StorSelfButton
            disabled={submitting}
            pending={submitting}
            variant="outlined"
            onClick={() => setSection(VerificationState.VERIFY_OTP)}
          >
            Cancel
          </StorSelfButton>
        )}
        <StorSelfButton pending={submitting} type="submit">
          {section === VerificationState.RESEND_OTP
            ? "Resend Code"
            : "Continue"}
        </StorSelfButton>
      </Box>
    </form>
  );
};

const phoneNumberValidator = yup
  .string()
  // must have 10 digits and allows for characters: -, (, ), and whitespace
  .length(10, "Must be 3 digit area code with 7 digit phone number.")
  .matches(/^[^a-zA-Z]+$/, "Invalid Phone number")
  .required("Phone Number is required.");

const schema = yup
  .object()
  .shape({
    phoneNumber: phoneNumberValidator,
  })
  .required();
